import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createMigrate, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { ViewSlug } from '~/types/components';
import type { Call, Contact, Group } from '~/types/models';
import { restSliceMigrations } from '~/types/states';
import type { INestedRestSlice, IRestSlice, IRestIndexParams } from '~/types/states';
import type { StringKeys } from '~/types/utils';

import { createCallsSlice, createContactsSlice } from '../nestedRestSlice/slice';

export const nestedContactsSlice = createContactsSlice('groups');
export const nestedCallsSlice = createCallsSlice('groups');

const initialState: IRestSlice<Group> & {
  calls: INestedRestSlice<Call>;
  contacts: INestedRestSlice<Contact>;
} = {
  columns: {
    index: ['sequence', 'name', 'contactsCount', 'callStrategy'],
    single: ['sequence', 'name', 'description', 'contactsCount', 'callStrategy', 'defaultCallDelay', 'callsCount', 'contactsCount'],
  },
  data: [],
  error: null,
  filters: {},
  initialItem: null,
  item: null,
  loading: true,
  page: 1,
  pageSize: 10,
  sortBy: '-sequence',
  total: null,
  totalPages: null,
  with: [],
  calls: nestedCallsSlice.state,
  contacts: nestedContactsSlice.state,
};

export const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    create: (_state, _action: PayloadAction<Partial<Group>>) => {},
    destroy: (_state, _action: PayloadAction<Group>) => {},
    index: (_state, _action: PayloadAction<IRestIndexParams<Group>>) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Group['id']; params?: IRestIndexParams<Group> }>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Group>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Group;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Group];
        } else {
          state.filters[k as keyof Group] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state, _action: PayloadAction<IRestIndexParams<Group>>) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Group>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Group>) => {},
  },
  extraReducers: (builder) => {
    builder.addDefaultCase((state, action) => {
      return {
        ...state,
        calls: nestedCallsSlice.reducer(state.calls, action),
        contacts: nestedContactsSlice.reducer(state.contacts, action),
      };
    });
  },
});

const { actions, getInitialState, reducer: groupsReducer } = groupsSlice;

const reducer = persistReducer(
  {
    key: 'CSN_Pointage:Groups',
    migrate: createMigrate(restSliceMigrations),
    storage,
    version: 0,
    whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
  },
  groupsReducer,
);

export { actions, getInitialState, reducer };
